import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Loading, ErrorState } from "@/components/common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Play, Pause, RotateCcw, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const ExList = ({ title, items, fallback, tint = "primary" }) => {
  const arr = Array.isArray(items) ? items : items ? [items] : [];
  if (arr.length === 0 && !fallback) return null;
  return (
    <div>
      <h3 className="mb-2 text-lg font-semibold">{title}</h3>
      {arr.length > 0 ? (
        <ul className="space-y-1.5">
          {arr.map((t, i) => (
            <li key={i} className="flex gap-2 text-sm leading-relaxed text-foreground/80">
              <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-${tint}`} />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="whitespace-pre-line text-sm leading-relaxed text-foreground/80">{fallback}</p>
      )}
    </div>
  );
};

export default function ExerciseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [exercise, setExercise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const [logging, setLogging] = useState(false);
  const [painNrs, setPainNrs] = useState(null);
  const timerRef = useRef(null);

  const [programId, setProgramId] = useState(null);
  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [ex, prog] = await Promise.allSettled([api.exercise(id), api.patientProgram(user.id)]);
      if (ex.status === "fulfilled") setExercise(ex.value); else throw ex.reason;
      if (prog.status === "fulfilled" && prog.value?.id) setProgramId(prog.value.id);
    } catch (e) { setError(errMsg(e)); }
    finally { setLoading(false); }
  }, [id, user.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (running) timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [running]);

  useEffect(() => {
    const target = exercise?.duration_seconds || exercise?.duration;
    if (target && seconds >= target && running) { setRunning(false); toast.success("Countdown completato!"); }
  }, [seconds, exercise, running]);

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const logExecution = async () => {
    if (!programId) { toast.error("Nessun programma attivo: impossibile registrare"); return; }
    setLogging(true);
    try {
      await api.logExercise({
        patient_id: user.id,
        program_id: programId,
        exercise_id: id,
        duration_seconds: seconds,
        sets_done: exercise?.sets || 0,
        reps_done: exercise?.reps || 0,
        ...(painNrs != null ? { pain_nrs: painNrs } : {}),
        completed: true,
      });
      toast.success("Esecuzione registrata!");
      navigate("/patient");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLogging(false); }
  };

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const target = exercise?.duration_seconds || exercise?.duration;
  const timerDone = !target || seconds >= target;
  const media = exercise?.image_url || exercise?.thumbnail || exercise?.video_url;

  return (
    <div className="mx-auto max-w-3xl">
      <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4" data-testid="back-button">
        <ArrowLeft className="mr-2 h-4 w-4" /> Indietro
      </Button>

      <Card className="overflow-hidden border-border/70">
        {exercise?.video_url ? (
          <video src={exercise.video_url} controls poster={exercise?.thumbnail_url || undefined} className="max-h-80 w-full bg-black object-contain" data-testid="exercise-video" />
        ) : media ? (
          <img src={media} alt={exercise?.title} className="h-56 w-full object-cover" />
        ) : null}
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            {exercise?.category && <Badge variant="secondary">{exercise.category}</Badge>}
            {exercise?.district && <Badge variant="secondary">{exercise.district}</Badge>}
            {exercise?.level && <Badge variant="outline">Livello: {exercise.level}</Badge>}
          </div>
          <CardTitle className="text-2xl">{exercise?.title || exercise?.name || "Esercizio"}</CardTitle>
          {exercise?.objective && <p className="text-sm text-muted-foreground">{exercise.objective}</p>}
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-wrap gap-4 text-sm">
            {exercise?.sets && <span className="rounded-lg bg-muted px-3 py-1.5"><b>{exercise.sets}</b> serie</span>}
            {exercise?.reps && <span className="rounded-lg bg-muted px-3 py-1.5"><b>{exercise.reps}</b> ripetizioni</span>}
            {target && <span className="rounded-lg bg-muted px-3 py-1.5"><b>{target}s</b> durata</span>}
          </div>

          {exercise?.description && (
            <p className="text-sm leading-relaxed text-foreground/80">{exercise.description}</p>
          )}

          <ExList title="Istruzioni" items={exercise?.instructions} fallback={exercise?.description} tint="primary" />

          {Array.isArray(exercise?.common_mistakes) && exercise.common_mistakes.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Errori comuni</p>
              <div className="rounded-2xl border border-border/70 bg-white p-4">
                <ul className="space-y-1.5 text-sm text-foreground/80">
                  {exercise.common_mistakes.map((t, i) => <li key={i} className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#d98a8a]" />{t}</li>)}
                </ul>
              </div>
            </div>
          )}

          {Array.isArray(exercise?.precautions) && exercise.precautions.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Precauzioni</p>
              <div className="rounded-2xl bg-[#fdf3d9] p-4">
                <ul className="space-y-1.5 text-sm text-[#8a6d3b]">
                  {exercise.precautions.map((t, i) => <li key={i} className="flex gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#d9a441]" />{t}</li>)}
                </ul>
              </div>
            </div>
          )}

          {/* Timer */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Timer esercizio</p>
            <div className="rounded-2xl border border-border/70 bg-white p-6 text-center" data-testid="exercise-timer">
              <p className="text-sm text-muted-foreground">{running ? "In corso" : "Pronto"} · durata {target ? fmt(target) : "libera"}</p>
              <p className="mt-1 font-mono text-5xl font-bold tabular-nums text-[#1f3a5f]">
                {target ? fmt(Math.max(target - seconds, 0)) : fmt(seconds)}
              </p>
              {target ? (
                <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${Math.min((seconds / target) * 100, 100)}%` }} />
                </div>
              ) : null}
              <div className="mt-5 flex justify-center gap-2">
                <Button className="rounded-full px-8" onClick={() => setRunning((r) => !r)} data-testid="timer-toggle">
                  {running ? <><Pause className="mr-2 h-4 w-4" /> Pausa</> : <><Play className="mr-2 h-4 w-4" /> Avvia</>}
                </Button>
                <Button variant="outline" className="rounded-full" onClick={() => { setRunning(false); setSeconds(0); }} data-testid="timer-reset">
                  <RotateCcw className="mr-2 h-4 w-4" /> Reset
                </Button>
              </div>
            </div>
          </div>

          {/* NRS pain */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Diario esecuzione</p>
            <div className="rounded-2xl border border-border/70 bg-white p-4">
              <p className="font-semibold text-[#1f3a5f]">Dolore durante l'esercizio</p>
              <p className="mb-3 text-xs text-muted-foreground">Scala NRS · Numeric Rating Scale</p>
              <div className="flex flex-wrap gap-2" data-testid="pain-scale">
                {Array.from({ length: 11 }).map((_, n) => (
                  <button key={n} onClick={() => setPainNrs(n)}
                    className={`h-10 w-10 rounded-xl border text-sm font-semibold transition-all ${painNrs === n ? "border-[#5f9c96] bg-[#e6f0e9] text-[#3f7a70]" : "border-border bg-white text-[#1f3a5f] hover:border-primary/40"}`}
                    data-testid={`pain-${n}`}>{n}</button>
                ))}
              </div>
              {painNrs != null && (
                <div className="mt-3 rounded-xl border border-[#cfe6d8] bg-[#eef7f1] px-3 py-2 text-sm font-medium text-[#3f7a70]">
                  {painNrs} — {painNrs === 0 ? "Nessun dolore" : painNrs <= 3 ? "Dolore lieve" : painNrs <= 6 ? "Dolore moderato" : "Dolore intenso"}
                </div>
              )}
            </div>
          </div>

          <div>
            <Button className="h-12 w-full text-base" onClick={logExecution} disabled={logging || !timerDone} data-testid="log-exercise-button">
              <CheckCircle2 className="mr-2 h-5 w-5" /> {logging ? "Registrazione..." : "Registra esecuzione"}
            </Button>
            {!timerDone && <p className="mt-2 text-center text-xs text-muted-foreground" data-testid="timer-lock-hint">Completa il timer dell'esercizio per poter registrare l'esecuzione.</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
